/* eslint-disable react/prop-types */
import React, {useState, useEffect, useCallback} from 'react';
import {
  Button,
  CircularProgress,
  TextField,
  IconButton,
  Tabs,
  Tab,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import {
  ArrowForward,
  ArrowBack,
  LocationOn,
  Search,
  CheckCircle,
  RadioButtonUnchecked,
} from '@mui/icons-material';

// ─── API ──────────────────────────────────────────────────────────────────────
const API_BASE = 'https://kops.apiireit.com/gateway';

// ─── Colors (shared) ─────────────────────────────────────────────────────────
const C = {
  azul: '#06448e',
  azulD: '#04316a',
  laranja: '#FF6600',
  laranjaD: '#cc4400',
  roxo: '#7c53e3',
  text: '#1a1a2e',
  muted: '#8a94a6',
  white: '#ffffff',
  success: '#059669',
  danger: '#e53935',
  warn: '#d97706',
  bg: '#f4f6fa',
};

// ─── Ponto card component ─────────────────────────────────────────────────────
const PontoCard = ({ponto, selected, onSelect}) => {
  const isSelected = selected && selected._id === ponto._id;
  const isPonto00 = ponto.codigoPonto?.toUpperCase() === 'PONTO00';

  return (
    <div
      onClick={() => onSelect(ponto)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 10px',
        borderRadius: 12,
        border: isSelected ? `2px solid ${C.laranja}` : '1px solid #e8eaf0',
        background: isSelected ? '#fff9f5' : C.white,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        boxShadow: isSelected ? `0 2px 8px ${C.laranja}22` : 'none',
      }}
    >
      {/* Imagem */}
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          overflow: 'hidden',
          flexShrink: 0,
          background: '#f0f2f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {ponto.imagem ? (
          <img
            src={ponto.imagem}
            alt={ponto.nome}
            style={{width: '100%', height: '100%', objectFit: 'cover'}}
            onError={e => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement.innerHTML =
                '<span style="font-size:20px">🏪</span>';
            }}
          />
        ) : (
          <span style={{fontSize: 20}}>🏪</span>
        )}
      </div>

      {/* Info */}
      <div style={{flex: 1, minWidth: 0}}>
        <p
          style={{
            margin: 0,
            fontSize: 13,
            fontWeight: 700,
            color: C.text,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {ponto.nome}
        </p>
        <p style={{margin: '1px 0 0', fontSize: 11, color: C.muted}}>
          {ponto.codigoPonto} · {ponto.bairro || ponto.cidade}, {ponto.estado}
        </p>
        {isPonto00 && (
          <p
            style={{
              margin: '2px 0 0',
              fontSize: 10,
              color: C.warn,
              fontWeight: 600,
            }}
          >
            Sem ponto fixo
          </p>
        )}
      </div>

      {/* Seleção */}
      <div style={{flexShrink: 0}}>
        {isSelected ? (
          <CheckCircle sx={{color: C.laranja, fontSize: 20}} />
        ) : (
          <RadioButtonUnchecked sx={{color: '#dde1ea', fontSize: 20}} />
        )}
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// STEP LOCALIZAÇÃO + PONTO
// ──────────────────────────────────────────────────────────────────────────────
/**
 * Props
 * ─────
 * sm              boolean
 * authUser        firebase user
 * getToken        () => Promise<string>
 * userLat         number | null
 * setUserLat      fn
 * userLng         number | null
 * setUserLng      fn
 * pontosProx      array
 * setPontosProx   fn
 * selectedPonto   object | null
 * setSelectedPonto fn
 * skipPonto       boolean
 * setSkipPonto    fn
 * onNext          () => void
 * onBack          () => void
 */
const StepLocation = ({
  authUser,
  getToken,
  userLat,
  setUserLat,
  userLng,
  setUserLng,
  pontosProx,
  setPontosProx,
  selectedPonto,
  setSelectedPonto,
  skipPonto,
  setSkipPonto,
  onNext,
  onBack,
}) => {
  const [tabValue, setTabValue] = useState(0); // 0 = próximos, 1 = por código
  const [searchCode, setSearchCode] = useState('');
  const [searching, setSearching] = useState(false);
  const [loadingPontos, setLoadingPontos] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);

  const hasPonto00 = pontosProx.some(
    ponto => ponto.codigoPonto?.toUpperCase() === 'PONTO00',
  );

  // ── Get user location ───────────────────────────────────────────────────────
  const getLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocalização não suportada pelo navegador.');
      return;
    }

    setLocationLoading(true);
    setError('');

    navigator.geolocation.getCurrentPosition(
      position => {
        const {latitude, longitude} = position.coords;
        setUserLat(latitude);
        setUserLng(longitude);
        setLocationLoading(false);
      },
      err => {
        console.error('Location error:', err);
        setError('Erro ao obter localização. Permita o acesso à localização.');
        setLocationLoading(false);
      },
      {enableHighAccuracy: true, timeout: 10000},
    );
  }, [setUserLat, setUserLng]);

  // ── Fetch nearby points ─────────────────────────────────────────────────────
  const fetchNearbyPoints = useCallback(async () => {
    if (!userLat || !userLng) return;

    setLoadingPontos(true);
    setError('');

    try {
      const token = await getToken();
      const res = await fetch(
        `${API_BASE}/parceiros/pontos/proximos?lat=${userLat}&lng=${userLng}&pageNo=0&pageSize=20`,
        {headers: {Authorization: `Bearer ${token}`}},
      );

      if (!res.ok) throw new Error('Erro ao buscar pontos próximos');

      const data = await res.json();
      setPontosProx(data.data || []);
    } catch (err) {
      console.error('Fetch nearby points error:', err);
      setError('Erro ao buscar pontos próximos.');
    } finally {
      setLoadingPontos(false);
    }
  }, [userLat, userLng, getToken, setPontosProx]);

  // ── Search point by code ────────────────────────────────────────────────────
  const searchByCode = useCallback(async () => {
    if (!searchCode.trim()) return;

    setSearching(true);
    setError('');

    try {
      const token = await getToken();
      const res = await fetch(
        `${API_BASE}/parceiros/pontos/get/codigo?codigoPonto=${searchCode.trim()}`,
        {headers: {Authorization: `Bearer ${token}`}},
      );

      if (!res.ok) throw new Error('Ponto não encontrado');

      const ponto = await res.json();
      setPontosProx([ponto]); // Show only this point
      setSelectedPonto(ponto);
    } catch (err) {
      console.error('Search by code error:', err);
      setError('Ponto não encontrado. Verifique o código.');
      setPontosProx([]);
    } finally {
      setSearching(false);
    }
  }, [searchCode, getToken, setPontosProx, setSelectedPonto]);

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (userLat && userLng && tabValue === 0) {
      fetchNearbyPoints();
    }
  }, [userLat, userLng, tabValue, fetchNearbyPoints]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError('');
    if (newValue === 1) {
      // Clear search when switching to code tab
      setSearchCode('');
      setPontosProx([]);
      setSelectedPonto(null);
    }
  };

  const handleSearchKeyPress = e => {
    if (e.key === 'Enter' && searchCode.trim().length >= 3) {
      searchByCode();
    }
  };

  const handleSelectPonto = ponto => {
    setSelectedPonto(ponto);
    setSkipPonto(false);
  };

  const handleSkipPonto = () => {
    setSelectedPonto(null);
    setSkipPonto(true);
  };

  const handleContinue = async () => {
    if (!selectedPonto && !skipPonto) {
      setError('Selecione um ponto ou escolha continuar sem vínculo.');
      return;
    }

    if (selectedPonto && authUser) {
      setSubmitting(true);
      setError('');
      try {
        const token = await getToken();
        const res = await fetch(
          `${API_BASE}/parceiros/pontos/vinculo/solicitar?firebaseid=${authUser.uid}&codigoPonto=${selectedPonto.codigoPonto}`,
          {method: 'PUT', headers: {Authorization: `Bearer ${token}`}},
        );
        if (!res.ok) throw new Error('Erro ao solicitar vínculo');
      } catch (err) {
        console.error('Solicitar vínculo error:', err);
        setError('Erro ao solicitar vínculo com o ponto. Tente novamente.');
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
    }

    onNext();
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      <Typography
        variant="h6"
        sx={{
          mb: 1,
          fontWeight: 800,
          color: C.text,
        }}
      >
        Localização e Vinculo a Ponto
      </Typography>

      <Typography
        variant="body2"
        sx={{
          mb: 3,
          color: C.muted,
          lineHeight: 1.55,
        }}
      >
        Usamos sua localização para exibir corridas próximas. Além disso,{' '}
        <strong style={{color: C.text}}>
          algumas categorias — como táxi e mototáxi — só aparecem para você se
          estiver vinculado a um ponto autorizado da cidade.
        </strong>{' '}
        Isso acontece porque certas prefeituras exigem vínculo com um ponto
        credenciado para operar legalmente. Se sua cidade tiver essa regra,
        vincule-se ao ponto correspondente; caso contrário, pode continuar sem
        vínculo.
      </Typography>

      {/* Location section */}
      <Box sx={{mb: 4}}>
        <Typography variant="subtitle1" sx={{fontWeight: 700, mb: 2}}>
          📍 Sua Localização
        </Typography>

        {userLat && userLng ? (
          <Alert severity="success" sx={{mb: 2}} icon={<LocationOn />}>
            Localização obtida: {userLat.toFixed(6)}, {userLng.toFixed(6)}
          </Alert>
        ) : (
          <Box sx={{mb: 2}}>
            <Button
              variant="outlined"
              startIcon={
                locationLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  <LocationOn />
                )
              }
              onClick={getLocation}
              disabled={locationLoading}
              sx={{
                borderColor: C.azul,
                color: C.azul,
                '&:hover': {borderColor: C.azulD, backgroundColor: '#f0f4ff'},
              }}
            >
              {locationLoading
                ? 'Obtendo localização...'
                : 'Permitir localização'}
            </Button>
          </Box>
        )}
      </Box>

      {/* Points section */}
      <Box>
        <Typography variant="subtitle1" sx={{fontWeight: 700, mb: 2}}>
          🏪 Vincular sua conta a um ponto (Opcional)
        </Typography>

        <Typography
          variant="body2"
          sx={{
            mb: 3,
            color: C.muted,
            lineHeight: 1.5,
          }}
        >
          Vincule-se a um ponto para receber corridas prioritárias nessa área. O
          código <strong>PONTO00</strong> pode aparecer na lista, mas ele é
          usado quando você não possui um ponto fixo. Se não tiver um ponto,
          escolha continuar sem vínculo.
        </Typography>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            mb: 3,
            '& .MuiTab-root': {textTransform: 'none', fontWeight: 600},
            '& .MuiTabs-indicator': {backgroundColor: C.laranja},
          }}
        >
          <Tab label="Pontos Próximos" />
          <Tab label="Buscar por Código" />
        </Tabs>

        {/* Tab content */}
        {tabValue === 0 && (
          <Box>
            {!userLat || !userLng ? (
              <Alert severity="info">
                Obtenha sua localização primeiro para ver pontos próximos.
              </Alert>
            ) : loadingPontos ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : pontosProx.length === 0 ? (
              <Alert severity="info">
                Nenhum ponto encontrado próximo à sua localização.
              </Alert>
            ) : (
              <Box>
                {hasPonto00 && (
                  <Alert severity="info" sx={{fontSize: 12, mb: 1, py: 0.5}}>
                    👇 <strong>PONTO00</strong> = sem ponto fixo.
                  </Alert>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.75,
                    maxHeight: 340,
                    overflowY: 'auto',
                    pr: 0.5,
                    '&::-webkit-scrollbar': {width: 4},
                    '&::-webkit-scrollbar-thumb': {
                      borderRadius: 4,
                      background: '#d0d8ee',
                    },
                  }}
                >
                  {pontosProx.map(ponto => (
                    <PontoCard
                      key={ponto._id}
                      ponto={ponto}
                      selected={selectedPonto}
                      onSelect={handleSelectPonto}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            <TextField
              fullWidth
              label="Código do Ponto"
              placeholder="Ex: PONTO01"
              value={searchCode}
              onChange={e => setSearchCode(e.target.value.toUpperCase())}
              onKeyPress={handleSearchKeyPress}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={searchByCode}
                    disabled={searching || searchCode.trim().length < 3}
                  >
                    {searching ? <CircularProgress size={20} /> : <Search />}
                  </IconButton>
                ),
              }}
              sx={{mb: 2}}
            />

            {pontosProx.length > 0 && (
              <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.75}}>
                {pontosProx.map(ponto => (
                  <PontoCard
                    key={ponto._id}
                    ponto={ponto}
                    selected={selectedPonto}
                    onSelect={handleSelectPonto}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Skip option */}
        <Box sx={{mt: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2}}>
          <Typography variant="body2" sx={{mb: 2, color: C.muted}}>
            Não quer se vincular agora?
          </Typography>
          <Button
            variant={skipPonto ? 'contained' : 'outlined'}
            onClick={handleSkipPonto}
            sx={{
              backgroundColor: skipPonto ? C.laranja : 'transparent',
              borderColor: C.laranja,
              color: skipPonto ? C.white : C.laranja,
              '&:hover': {
                backgroundColor: skipPonto ? C.laranjaD : '#fff9f5',
                borderColor: C.laranja,
              },
            }}
          >
            Continuar sem vínculo
          </Button>
        </Box>
      </Box>

      {/* Error display */}
      {error && (
        <Alert severity="error" sx={{mt: 2}}>
          {error}
        </Alert>
      )}

      {/* Navigation */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mt: 4,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {onBack && (
          <Button
            onClick={onBack}
            startIcon={<ArrowBack />}
            sx={{textTransform: 'none', color: C.muted}}
          >
            Voltar
          </Button>
        )}
        <Button
          onClick={handleContinue}
          endIcon={
            submitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <ArrowForward />
            )
          }
          disabled={submitting}
          variant="contained"
          sx={{
            bgcolor: C.laranja,
            color: '#fff',
            borderRadius: '12px',
            height: 48,
            px: 3,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 15,
            ':hover': {bgcolor: C.laranjaD},
          }}
        >
          {submitting ? 'Enviando...' : 'Continuar'}
        </Button>
      </Box>
    </div>
  );
};

export default StepLocation;
